import type { DocumentAnnotation, DocumentAnnotationStatus } from "./DocumentAnnotations";

const MAX_COMPACT_QUOTE_CHARACTERS = 1000;
/** Share of the smaller box height two boxes must overlap vertically to count as one line. */
const MIN_LINE_OVERLAP_RATIO = .6;

export interface DocumentTextAnnotation { id: string; quotes: readonly string[]; contexts?: readonly string[]; compact?: boolean; label: string; status: DocumentAnnotationStatus }
interface TextRectangle { x: number; y: number; width: number; height: number }
export interface DocumentTextBox extends TextRectangle { text: string; characters?: readonly (TextRectangle & { start: number; end: number })[] }
export interface DocumentTextPage { page: number; boxes: readonly DocumentTextBox[] }
const normalized = (value: string) => value.normalize("NFKC").replace(/\s+/g, " ").trim();
const word = (value: string | undefined) => value != null && /[-\p{L}\p{N}_/]/u.test(value);

function occurrences(text: string, quote: string, compact: boolean) {
  if (compact) {
    const characters = Array.from(quote.replace(/\s/g, ""));
    if (!characters.length || characters.length > MAX_COMPACT_QUOTE_CHARACTERS) return [];
    const pattern = characters.map(character => character.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("\\s*");
    return [...text.matchAll(new RegExp(pattern, "gu"))].map(match => ({ at: match.index, length: match[0].length }));
  }
  const result = [];
  for (let at = text.indexOf(quote); at >= 0; at = text.indexOf(quote, at + 1)) result.push({ at, length: quote.length });
  return result;
}

/** Preserve original offsets through ligatures and collapsed whitespace. */
function textMap(raw: string) {
  const spans: { start: number; end: number }[] = [];
  let text = "", start = 0;
  for (const character of raw) {
    const end = start + character.length;
    for (const part of character.normalize("NFKC")) {
      const space = /\s/u.test(part);
      if (space && (!text || text.endsWith(" "))) { if (text.endsWith(" ")) spans[spans.length - 1].end = end; continue; }
      text += space ? " " : part;
      for (let unit = 0; unit < (space ? 1 : part.length); unit++) spans.push({ start, end });
    }
    start = end;
  }
  if (text.endsWith(" ")) { text = text.slice(0, -1); spans.pop(); }
  return { text, spans };
}

function lines(boxes: readonly TextRectangle[]): TextRectangle[] {
  const rows: TextRectangle[] = [];
  for (const box of boxes) {
    const row = rows.find(row => Math.min(row.y + row.height, box.y + box.height) - Math.max(row.y, box.y) > Math.min(row.height, box.height) * MIN_LINE_OVERLAP_RATIO
      && Math.max(row.x, box.x) - Math.min(row.x + row.width, box.x + box.width) <= Math.max(row.height, box.height));
    if (!row) { rows.push({ x: box.x, y: box.y, width: box.width, height: box.height }); continue; }
    const right = Math.max(row.x + row.width, box.x + box.width), bottom = Math.max(row.y + row.height, box.y + box.height);
    row.x = Math.min(row.x, box.x); row.y = Math.min(row.y, box.y); row.width = right - row.x; row.height = bottom - row.y;
  }
  return rows;
}

/** Only unique source locations are highlighted; exact context can disambiguate repeated values. */
export function locateDocumentText(pages: readonly DocumentTextPage[], targets: readonly DocumentTextAnnotation[]): DocumentAnnotation[] {
  return targets.flatMap(target => {
    const matches = new Map<string, { annotations: DocumentAnnotation[]; contextual: boolean }>();
    for (const page of pages) {
      let text = "";
      const ranges = page.boxes.map(box => { const map = textMap(box.text), start = text.length; text += map.text + " "; return { start, end: start + map.text.length, box, map }; });
      for (const raw of target.quotes) {
        const quote = normalized(raw);
        if (!quote) continue;
        for (const { at, length } of occurrences(text, quote, target.compact === true)) {
          if (word(quote[0]) && word(text[at - 1]) || word(quote.at(-1)) && word(text[at + length])) continue;
          const boxes: TextRectangle[] = [];
          let unmeasured = false;
          for (const range of ranges.filter(range => range.end > at && range.start < at + length)) {
            const from = Math.max(0, at - range.start), to = Math.min(range.map.text.length, at + length - range.start);
            if (range.box.characters) {
              const start = range.map.spans[from]?.start, end = range.map.spans[to - 1]?.end;
              boxes.push(...range.box.characters.filter(character => start != null && end != null && character.end > start && character.start < end));
            } else if (from === 0 && to === range.map.text.length) boxes.push(range.box);
            else unmeasured = true;
          }
          if (unmeasured || !boxes.length) continue;
          const annotations = lines(boxes).filter(box => box.width > 0 && box.height > 0).map(box => ({ ...box, id: target.id, label: target.label, status: target.status, page: page.page }));
          const contextual = (target.contexts ?? []).some(raw => {
            const context = normalized(raw);
            if (!context || context === quote) return false;
            for (let location = text.indexOf(context); location >= 0; location = text.indexOf(context, location + 1))
              if (location <= at && location + context.length >= at + length) return true;
            return false;
          });
          matches.set(JSON.stringify(annotations), { annotations, contextual });
        }
      }
    }
    const candidates = [...matches.values()], contextual = candidates.filter(candidate => candidate.contextual);
    const locations = contextual.length ? contextual : candidates;
    return locations.length === 1 ? locations[0].annotations : [];
  });
}
